/**
 * Simple offline mutation queue backed by IndexedDB.
 * When online, mutations pass through immediately.
 * When offline, POST/PATCH/PUT/DELETE are enqueued and flushed on reconnect.
 *
 * Uses a per-op idempotency key to be retry-safe.
 */
import { openDB } from "idb";
import { getToken, API } from "@/lib/api";

const DB_NAME = "fender-offline";
const STORE = "queue";

const dbPromise = openDB(DB_NAME, 1, {
  upgrade(db) {
    if (!db.objectStoreNames.contains(STORE)) {
      db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    }
  },
});

const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const enqueue = async (op) => {
  const db = await dbPromise;
  const id = crypto?.randomUUID?.() || String(Date.now() + Math.random());
  const record = {
    idempotency_key: id,
    method: op.method,
    url: op.url,
    body: op.body || null,
    headers: op.headers || {},
    label: op.label || `${op.method} ${op.url}`,
    created_at: Date.now(),
    tries: 0,
  };
  await db.add(STORE, record);
  emit();
  return record;
};

export const pendingCount = async () => {
  const db = await dbPromise;
  return db.count(STORE);
};

export const listPending = async () => {
  const db = await dbPromise;
  return db.getAll(STORE);
};

const send = async (rec) => {
  const token = getToken();
  const url = rec.url.startsWith("http") ? rec.url : `${API}${rec.url}`;
  const resp = await fetch(url, {
    method: (rec.method || "POST").toUpperCase(),
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": rec.idempotency_key,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(rec.headers || {}),
    },
    body: rec.body ? JSON.stringify(rec.body) : undefined,
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json().catch(() => ({}));
};

let flushing = false;
let fastInterval = null;
const scheduleFast = () => {
  if (fastInterval) return;
  fastInterval = setInterval(async () => {
    const n = await pendingCount();
    if (n === 0) {
      clearInterval(fastInterval);
      fastInterval = null;
      return;
    }
    if (navigator.onLine) flush();
  }, 5000);
};

export const flush = async () => {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const db = await dbPromise;
    let all = await db.getAll(STORE);
    for (const rec of all) {
      try {
        await send(rec);
        await db.delete(STORE, rec.id);
      } catch (e) {
        rec.tries += 1;
        rec.last_error = e.message;
        await db.put(STORE, rec);
        if (rec.tries > 5) {
          await db.delete(STORE, rec.id);
          console.warn("[fender-offline] dropping record after max retries", rec);
        }
        break;
      }
    }
    // if any records remain, schedule fast retry
    const remaining = await db.count(STORE);
    if (remaining > 0) scheduleFast();
  } finally {
    flushing = false;
    emit();
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    // multi-attempt flush on reconnection because the 'online' event
    // may fire before the actual network is fully reachable
    setTimeout(flush, 200);
    setTimeout(flush, 1500);
    setTimeout(flush, 5000);
    scheduleFast();
  });
  setInterval(() => { if (navigator.onLine) flush(); }, 15000);
  setTimeout(() => { if (navigator.onLine) flush(); }, 1500);
}
